/**
 * BlankThemeDialog
 *
 * In-editor entry point for the Blank / Random-Layout generator. Regenerates the
 * currently open photobook / layflat design so every interior (non-cover) page
 * gets a random, non-repeating layout with a user-chosen number of photo boxes.
 *
 * Two modes:
 *   • "empty" — clears the interior pages, leaving empty photo boxes (a fresh
 *     skeleton). Destructive when the design already has photos → confirm step.
 *   • "keep"  — reflows the design's existing photos into the new layouts.
 *
 * Cover pages are never modified. Admins can additionally change the number of
 * spreads; customers regenerate the current page count.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BiShuffle } from "react-icons/bi";
import { BsGrid1X2, BsImages, BsExclamationTriangleFill } from "react-icons/bs";
import { USER_TYPES } from "../../library/utils/constants/index.js";
import {
  generateRandomLayoutPages,
  getCurrentInteriorPageCount,
  productUsesSpreads,
} from "../../library/utils/helpers/blankThemeGenerator.js";

// ── Styled shells (mirror CreateThemeDialog's design language) ───────────────
const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 500px;
  }
  .modal-content {
    border-radius: 14px;
    border: none;
    box-shadow: 0 12px 44px rgba(0, 0, 0, 0.22);
    overflow: hidden;
  }
  .modal-header {
    border-bottom: none;
    padding: 18px 24px;
    background: linear-gradient(
      135deg,
      var(--primary) 0%,
      color-mix(in srgb, var(--primary) 78%, #000) 100%
    );
  }
  .modal-title {
    font-weight: 600;
    color: #fff;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 9px;
  }
  .modal-body {
    padding: 16px 20px;
  }
  .modal-footer {
    border-top: 1px solid #eee;
    padding: 11px 20px;
    gap: 8px;
  }
  .btn-close {
    filter: brightness(0) invert(1);
  }
`;

const Section = styled.div`
  margin-bottom: 14px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 12.5px;
  margin-bottom: 7px;
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const ModeCard = styled.button`
  text-align: left;
  border: 1.5px solid ${(p) => (p.$active ? "var(--primary)" : "#e3e6ea")};
  background: ${(p) =>
    p.$active
      ? "color-mix(in srgb, var(--primary) 8%, #fff)"
      : "#fff"};
  border-radius: 9px;
  padding: 8px 9px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:hover {
    border-color: ${(p) => (p.$active ? "var(--primary)" : "#c3c8cf")};
  }

  .mode-icon {
    color: var(--primary);
    font-size: 15px;
    margin-bottom: 1px;
  }
  .mode-title {
    font-weight: 600;
    font-size: 12px;
    color: #222;
  }
  .mode-sub {
    font-size: 10.5px;
    color: #6b7178;
    line-height: 1.3;
  }
`;

const CountRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const CountField = styled.div`
  flex: 1;
  min-width: 120px;

  label {
    font-size: 12px;
    color: #6b7178;
    margin-bottom: 5px;
    display: block;
  }
`;

const NumInput = styled(Form.Control)`
  border-radius: 8px;
  padding: 9px 12px;
  border: 1px solid #dee2e6;
  font-size: 15px;
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 0.2rem rgba(64, 132, 181, 0.22);
  }
`;

const Summary = styled.div`
  background: #f4f8fb;
  border: 1px solid #e5eef5;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12.5px;
  color: #46525c;
  line-height: 1.5;
`;

const WarnBanner = styled.div`
  background: #fff5f3;
  border: 1px solid #f4c9bf;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12.5px;
  color: #9a3412;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #e0592f;
  }
`;

const CancelBtn = styled(Button)`
  background: transparent;
  border: 1px solid #ccc;
  color: #555;
  font-weight: 500;
  font-size: 13px;
  border-radius: 8px;
  padding: 6px 14px;
  &:hover {
    background: #f6f7f8;
    border-color: #b3b3b3;
    color: #333;
  }
`;

const GenerateBtn = styled(Button)`
  border: none;
  font-weight: 600;
  font-size: 13px;
  border-radius: 8px;
  padding: 6px 16px;
  background: ${(p) => (p.$danger ? "#e0592f" : "var(--primary)")};
  &:hover {
    background: ${(p) =>
      p.$danger
        ? "color-mix(in srgb, #e0592f 88%, #000)"
        : "color-mix(in srgb, var(--primary) 85%, #000)"};
  }
  &:disabled {
    background: #c9ccd0;
  }
`;

const MIN_BOX = 1;
const MAX_BOX = 20;
const clampInt = (v, lo, hi, fallback) => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
};

const BlankThemeDialog = ({ show, onClose, onGenerated }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.projectSetup.userDetails);
  const pages = useSelector((state) => state.canvas.present.pages);

  const isAdmin =
    user != null &&
    (user.userTypeCode === USER_TYPES.SUPERUSER ||
      user.userTypeCode === USER_TYPES.ADMIN ||
      user.userTypeCode === USER_TYPES.EMPLOYEE);

  const [mode, setMode] = useState("keep"); // "keep" | "empty"
  const [minImages, setMinImages] = useState("1");
  const [maxImages, setMaxImages] = useState("4");
  const [spreadCount, setSpreadCount] = useState("");
  const [countScope, setCountScope] = useState("spread"); // "spread" | "page"
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Photobook/foldable lay out in 2-page spreads → offer per-spread vs per-page.
  const hasSpreads = useMemo(
    () => (show ? productUsesSpreads() : false),
    [show, pages] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const currentInterior = useMemo(
    () => (show ? getCurrentInteriorPageCount() : 0),
    [show, pages] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Seed the fields when the dialog opens.
  useEffect(() => {
    if (!show) return;
    setMode("keep");
    setMinImages("1");
    setMaxImages("4");
    setSpreadCount(String(currentInterior || 0));
    setCountScope("spread");
    setGenerating(false);
    setConfirming(false);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  // Does the current design hold any placed photo? (drives the destructive warning)
  const hasPhotos = useMemo(() => {
    if (!show || !Array.isArray(pages)) return false;
    return pages.some((p) =>
      p?.layout?.some((l) =>
        l?.objects?.some((o) => o?.type === "img" && (o.url ?? "") !== "")
      )
    );
  }, [show, pages]);

  const targetSpreads = isAdmin
    ? clampInt(spreadCount, 0, 300, currentInterior)
    : currentInterior;

  // Plain-English phrase for how photos are counted (avoids the word "spread").
  const scopePhrase = !hasSpreads
    ? "on each page"
    : countScope === "spread"
      ? "on both pages together"
      : "on each page";

  // Nothing to generate — a brand-new book can start with 0 interior pages.
  const nothingToDo = targetSpreads <= 0;

  // Destructive outcomes that warrant an explicit confirm step:
  //  • "empty" mode on a design that already holds photos, or
  //  • an admin shrinking the page count (drops those pages + their photos).
  const willClearPhotos = mode === "empty" && hasPhotos;
  const willReduce = isAdmin && targetSpreads < currentInterior;
  const willWarn = !nothingToDo && (willClearPhotos || willReduce);

  const droppedCount = Math.max(0, currentInterior - targetSpreads);

  const runGeneration = async () => {
    setGenerating(true);
    try {
      const lo = clampInt(minImages, MIN_BOX, MAX_BOX, 1);
      const hi = clampInt(maxImages, MIN_BOX, MAX_BOX, Math.max(lo, 4));
      await generateRandomLayoutPages(dispatch, {
        mode,
        minImages: Math.min(lo, hi),
        maxImages: Math.max(lo, hi),
        targetInteriorCount: isAdmin ? targetSpreads : undefined,
        countScope,
        // This dialog IS the Layout tab's "Generate" button, so it draws from
        // the ticked pool like Shuffle All next to it.
        autoLayoutOnly: true,
      });
      // The generator records the whole run as ONE undo step (no clearHistory),
      // so Ctrl+Z / the toolbar Undo revert the generation in a single step.
      toast.success("Blank design generated!", {
        position: "top-center",
        autoClose: 1200,
      });
      // Hand the used settings back so the caller can offer a one-click re-roll.
      onGenerated?.({
        mode,
        minImages: Math.min(lo, hi),
        maxImages: Math.max(lo, hi),
      });
      onClose();
    } catch (err) {
      console.error("Blank design generation failed:", err);
      toast.error(err?.message || "Could not generate design. Please try again.", {
        position: "top-center",
      });
    } finally {
      setGenerating(false);
      setConfirming(false);
    }
  };

  const handleGenerateClick = () => {
    if (willWarn && !confirming) {
      setConfirming(true);
      return;
    }
    runGeneration();
  };

  // "Back" (while confirming) steps back to the form; "Cancel" closes.
  const handleCancelClick = () => {
    if (generating) return;
    if (confirming) {
      setConfirming(false);
      return;
    }
    onClose();
  };

  // Escape / backdrop: step back from confirm rather than discarding the form.
  const handleHide = () => {
    if (generating) return;
    if (confirming) {
      setConfirming(false);
      return;
    }
    onClose();
  };

  // Reflect the clamped/valid value in the input on blur, so what's shown
  // matches what the summary and generation will actually use.
  const commitMin = () =>
    setMinImages(String(clampInt(minImages, MIN_BOX, MAX_BOX, 1)));
  const commitMax = () =>
    setMaxImages(String(clampInt(maxImages, MIN_BOX, MAX_BOX, 4)));

  const loBound = clampInt(minImages, MIN_BOX, MAX_BOX, 1);
  const hiBound = clampInt(maxImages, MIN_BOX, MAX_BOX, Math.max(loBound, 4));
  const rangeLabel =
    Math.min(loBound, hiBound) === Math.max(loBound, hiBound)
      ? `${loBound}`
      : `${Math.min(loBound, hiBound)}–${Math.max(loBound, hiBound)}`;

  return (
    <StyledModal
      show={show}
      onHide={handleHide}
      centered
      backdrop="static"
      keyboard={!generating}
    >
      <Modal.Header closeButton={!generating}>
        <Modal.Title>
          <BiShuffle size={18} /> Auto-Arrange My Pages
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* What happens to existing photos */}
        <Section>
          <Label>What should happen to your photos?</Label>
          <ModeGrid role="radiogroup" aria-label="What should happen to your photos?">
            <ModeCard
              type="button"
              role="radio"
              aria-checked={mode === "keep"}
              $active={mode === "keep"}
              onClick={() => {
                setMode("keep");
                setConfirming(false);
              }}
              disabled={generating}
            >
              <BsImages className="mode-icon" />
              <span className="mode-title">Keep my photos</span>
              <span className="mode-sub">
                Rearrange the photos I already added into new designs.
              </span>
            </ModeCard>
            <ModeCard
              type="button"
              role="radio"
              aria-checked={mode === "empty"}
              $active={mode === "empty"}
              onClick={() => {
                setMode("empty");
                setConfirming(false);
              }}
              disabled={generating}
            >
              <BsGrid1X2 className="mode-icon" />
              <span className="mode-title">Start empty</span>
              <span className="mode-sub">
                Make blank photo spaces I'll fill in later.
              </span>
            </ModeCard>
          </ModeGrid>
        </Section>

        {/* Count across both facing pages, or per single page (photobook only) */}
        {hasSpreads && (
          <Section>
            <Label>Count photos across…</Label>
            <ModeGrid role="radiogroup" aria-label="Count photos across">
              <ModeCard
                type="button"
                role="radio"
                aria-checked={countScope === "spread"}
                $active={countScope === "spread"}
                onClick={() => setCountScope("spread")}
                disabled={generating}
              >
                <span className="mode-title">Both pages</span>
                <span className="mode-sub">
                  The number covers the two facing pages together.
                </span>
              </ModeCard>
              <ModeCard
                type="button"
                role="radio"
                aria-checked={countScope === "page"}
                $active={countScope === "page"}
                onClick={() => setCountScope("page")}
                disabled={generating}
              >
                <span className="mode-title">Each page</span>
                <span className="mode-sub">
                  The number is per page — two pages get about double.
                </span>
              </ModeCard>
            </ModeGrid>
          </Section>
        )}

        {/* How many photos */}
        <Section>
          <Label>How many photos {scopePhrase}?</Label>
          <CountRow>
            <CountField>
              <label htmlFor="blankgen-min">Minimum</label>
              <NumInput
                id="blankgen-min"
                type="number"
                min={MIN_BOX}
                max={MAX_BOX}
                value={minImages}
                onChange={(e) => setMinImages(e.target.value)}
                onBlur={commitMin}
                disabled={generating}
              />
            </CountField>
            <CountField>
              <label htmlFor="blankgen-max">Maximum</label>
              <NumInput
                id="blankgen-max"
                type="number"
                min={MIN_BOX}
                max={MAX_BOX}
                value={maxImages}
                onChange={(e) => setMaxImages(e.target.value)}
                onBlur={commitMax}
                disabled={generating}
              />
            </CountField>
          </CountRow>
        </Section>

        {/* Admin-only: how many pages */}
        {isAdmin && (
          <Section>
            <Label as="label" htmlFor="blankgen-spreadcount">
              How many pages?
            </Label>
            <CountRow>
              <CountField style={{ minWidth: "100%" }}>
                <NumInput
                  id="blankgen-spreadcount"
                  type="number"
                  min={0}
                  max={300}
                  value={spreadCount}
                  onChange={(e) => setSpreadCount(e.target.value)}
                  disabled={generating}
                />
                <div style={{ marginTop: 6, fontSize: 12, color: "#7a828b" }}>
                  You have {currentInterior} now. Front and back covers are
                  handled for you.
                </div>
              </CountField>
            </CountRow>
          </Section>
        )}

        {/* Summary */}
        <Section>
          {nothingToDo ? (
            <Summary>
              There are no pages to fill in yet.{" "}
              {isAdmin
                ? "Set how many pages you want above."
                : "Add at least one page first."}
            </Summary>
          ) : (
            <Summary>
              This will arrange <strong>{targetSpreads}</strong> pages with{" "}
              <strong>{rangeLabel}</strong>{" "}
              {mode === "empty" ? "empty " : ""}photo
              {rangeLabel === "1" ? "" : "s"} {scopePhrase}
              {mode === "keep" ? ", keeping your photos" : ""}. Your covers won't
              change. Press Ctrl+Z to undo.
            </Summary>
          )}
        </Section>

        {/* Destructive warning */}
        {willWarn && (
          <Section>
            <WarnBanner role="alert">
              <BsExclamationTriangleFill size={15} />
              <span>
                {confirming && (
                  <strong style={{ display: "block", marginBottom: 2 }}>
                    Replace your current design? This can't be undone.
                  </strong>
                )}
                {willReduce && willClearPhotos ? (
                  <>
                    This removes {droppedCount} pages and clears the photos from
                    the rest.
                  </>
                ) : willReduce ? (
                  <>
                    This removes {droppedCount} page
                    {droppedCount === 1 ? "" : "s"} — the photos on{" "}
                    {droppedCount === 1 ? "it" : "them"} will be lost.
                  </>
                ) : (
                  <>
                    This clears the photos on {currentInterior} pages. Pick{" "}
                    <strong>Keep my photos</strong> to keep them.
                  </>
                )}
              </span>
            </WarnBanner>
          </Section>
        )}
      </Modal.Body>
      <Modal.Footer>
        <CancelBtn onClick={handleCancelClick} disabled={generating}>
          {confirming ? "Back" : "Cancel"}
        </CancelBtn>
        <GenerateBtn
          onClick={handleGenerateClick}
          disabled={generating || nothingToDo}
          $danger={confirming}
        >
          {generating ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Arranging…
            </>
          ) : confirming ? (
            "Yes, replace"
          ) : (
            "Arrange Pages"
          )}
        </GenerateBtn>
      </Modal.Footer>
    </StyledModal>
  );
};

export default BlankThemeDialog;
